import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import Select from '@/components/forms/Select'

import { applyMigration } from '@/reducers/costimizer/costimizer.actions'
import { selectRun } from '@/reducers/costimizer/costimizer.redux'

import {
  criteriaPassingAt,
  formatPercent,
  nPricedSubjects,
  NO_THRESHOLD,
  RECOMMENDED_TOLERANCE,
  scorecardSavingPct,
  TOLERANCE_OPTIONS,
} from '../Costimizer.helpers'

const optionLabel = (points) => {
  if (points === NO_THRESHOLD) {
    return 'No threshold — migrate every prompt tested'
  }

  if (points === RECOMMENDED_TOLERANCE) {
    return `${points} points (recommended)`
  }

  return `${points} point${points === 1 ? '' : 's'}`
}

const toleranceOptions = TOLERANCE_OPTIONS.map((points) => ({
  value: points,
  label: optionLabel(points),
}))

/**
 * Putting a threshold's worth of prompts live, in one write.
 *
 * Driven by the threshold beside it — including "no threshold", which takes everything measured. It confirms
 * first, since this changes what customers are scored by. A single prompt outside the threshold goes through
 * its own row instead (`ApplyPromptButton`).
 *
 * ⚠ The threshold is the caller's state, not this component's. Every prompt's status in the table above
 * depends on it too, so one number has to drive both or a row can claim the opposite of what this button is
 * about to do.
 *
 * @param {number|string} props.tolerancePct points of accuracy to accept, or `NO_THRESHOLD`
 * @param {Function} props.onToleranceChange called with the newly chosen threshold
 */
export const ApplyMigration = ({
  scorecardConfigId,
  subjects,
  criteria,
  tolerancePct,
  onToleranceChange,
}) => {
  const dispatch = useDispatch()
  const run = useSelector(selectRun(scorecardConfigId))
  const [confirming, setConfirming] = useState(false)

  const applying = criteriaPassingAt(criteria, tolerancePct)
  const count = applying.length
  const includesWorse = applying.some((criterion) => criterion.marginPct < -RECOMMENDED_TOLERANCE)
  const savingPct = scorecardSavingPct(subjects, criteria, tolerancePct)
  const priced = nPricedSubjects(subjects, criteria)

  const handleApply = () => {
    dispatch(applyMigration(scorecardConfigId, { criteria: applying }))
    setConfirming(false)
  }

  return (
    <div className="costimizer-apply flex-align-end medium-gap">
      <FormField
        label="Accept prompts within"
        className="costimizer-apply-tolerance"
        data-testid="costimizer-tolerance"
      >
        <Select
          options={toleranceOptions}
          isClearable={false}
          value={toleranceOptions.find((option) => option.value === tolerancePct)}
          onChange={(option) => onToleranceChange(option.value)}
        />
      </FormField>

      <Button
        brand
        disabled={isEmpty(applying)}
        loading={run.applying}
        onClick={() => setConfirming(true)}
        data-testid="costimizer-apply"
      >
        {`Apply ${count} change${count === 1 ? '' : 's'}`}
      </Button>

      {/* Says SCORECARD, because that is the question — not "how much cheaper is the average prompt", which
          is a different number whenever prompts cost different amounts. `scorecardSavingPct` is cost-weighted
          for that reason.

          And it says how many prompts it could price, whenever that is not all of them: on a partly tested
          scorecard the figure is a percentage of a smaller bill than the reader is looking at.

          ⚠ NOTHING AT ALL when it could price none of them, rather than a dash. Both numbers here count only
          the prompts still TO migrate, so on a scorecard whose one measured prompt has already been applied
          this read "— cheaper across the 0 of 1 prompts with measured costs" directly beneath a table
          showing that prompt's cost change. A sentence about a saving nobody can compute is worse than no
          sentence. */}
      {!isNil(savingPct) && (
        <span className="costimizer-apply-estimate" data-testid="costimizer-estimated-saving">
          <strong>{formatPercent(savingPct)}</strong>
          <span className="muted-text">
            {priced === subjects.length
              ? ' cheaper to score a call on this scorecard'
              : ` cheaper across the ${priced} of ${subjects.length} prompts with measured costs`}
          </span>
        </span>
      )}

      <BasicModal
        data-testid="costimizer-apply-confirm"
        title="Apply these prompts?"
        show={confirming}
        onClose={() => setConfirming(false)}
        size="tiny"
      >
        <p>
          {`This puts ${count} prompt${count === 1 ? '' : 's'} live on this scorecard. Calls scored from now on will use `}
          {count === 1 ? 'it' : 'them'}.
        </p>
        {includesWorse && (
          <p>
            {`This threshold includes prompts that measured more than ${RECOMMENDED_TOLERANCE} points `}
            <strong>worse</strong>
            {
              ' than what is running now — further off than a re-run of the current prompt explains.'
            }
          </p>
        )}
        <div className="form-actions is-modal">
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button brand onClick={handleApply} data-testid="costimizer-apply-confirm-button">
            Apply
          </Button>
        </div>
      </BasicModal>
    </div>
  )
}
